import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../api/axios';
import {
  CheckCircle,
  Lock,
  ArrowRight,
  ArrowLeft,
  Sparkles
} from 'lucide-react';
import { Button, GlassCard, Footer } from './ui';
import { TIERS } from '../utils/tierUtils';
import UpgradeModal from './UpgradeModal';

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

function PricingPage() {
  const navigate = useNavigate();
  const [, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [error, setError] = useState(null);
  
  // Fetch user info with tier from API
  useEffect(() => {
    setError(null); // Clear any previous errors
    const token = localStorage.getItem('token');
    if (token) {
      api.get('/api/verify')
        .then(response => {
          setUser({
            ...response.data,
            tier: response.data.tier || TIERS.FREE,
            subscription_expires_at: response.data.subscription_expires_at || null
          });
        })
        .catch((error) => {
          console.error('Token verification failed:', error);
          // Clear invalid token so user is treated as logged out
          localStorage.removeItem('token');
          setUser({ tier: TIERS.FREE });
        })
        .finally(() => setLoading(false));
    } else {
      setUser({ tier: TIERS.FREE });
      setLoading(false);
    }
  }, []);

  const handleGetStarted = async (tier) => {
    if (tier === TIERS.PRO) {
      // Redirect to Paystack checkout
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        const response = await api.post('/api/initialize-payment');
        
        if (response.data.authorization_url) {
          // Redirect to Paystack checkout
          window.location.href = response.data.authorization_url;
        }
      } catch (error) {
        console.error('Error initializing payment:', error);
        if (error.response?.status === 401) {
          // Session/token is invalid – clear it and redirect to login
          localStorage.removeItem('token');
          setError('Your session has expired. Please log in again to start checkout.');
          navigate('/login');
        } else {
          setError(error.response?.data?.error || 'Failed to start checkout. Please try again.');
        }
      }
    } else {
      // Free tier - navigate to upload or login
      const token = localStorage.getItem('token');
      if (token) {
        navigate('/upload');
      } else {
        navigate('/login');
      }
    }
  };

  const handleUpgrade = () => {
    setShowUpgradeModal(false);
    handleGetStarted(TIERS.PRO);
  };

  const pricingTiers = [
    {
      name: 'Free',
      price: '$0',
      tier: TIERS.FREE,
      description: 'Perfect for getting started',
      features: [
        { text: 'Generate quotes and Captions', available: true },
        { text: 'Download quotecard on review page', available: true },
        { text: 'Up to 5 quotecards', available: true },
        { text: 'Generate Audiogram', available: false },
        { text: 'Send promotion kit to guest', available: false },
      ],
      highlight: false,
    },
    {
      name: 'Pro',
      price: '$9.99',
      tier: TIERS.PRO,
      description: 'For serious podcasters',
      badge: 'Most Popular',
      features: [
        { text: 'Generates unlimited audiograms with captions', available: true },
        { text: 'Generates unlimited quotes and Captions', available: true },
        { text: 'Unlimited quotecards', available: true },
        { text: 'Send promotion kit to guest', available: true },
        { text: 'Download audiogram on review page', available: true },
        { text: 'Direct share to guests social media', available: true, comingSoon: true },
      ],
      highlight: true,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-zinc-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900 to-zinc-950" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-emerald-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-emerald-500/5 rounded-full blur-3xl" />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(16,185,129,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(16,185,129,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

      {/* Back Button */}
      <motion.button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 z-20 flex items-center gap-2 text-zinc-400 hover:text-zinc-100 transition-colors"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        whileHover={{ x: -4 }}
      >
        <ArrowLeft className="w-5 h-5" />
        <span className="font-medium">Back</span>
      </motion.button>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-20">
        {/* Error Message */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-2xl mx-auto mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl"
          >
            <p className="text-red-400 text-center">{error}</p>
          </motion.div>
        )}

        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial="initial"
          animate="animate"
          variants={fadeInUp}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Sparkles className="w-4 h-4" />
            Choose Your Plan
          </motion.div>

          <motion.h1
            className="text-4xl md:text-6xl font-bold text-zinc-100 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            Simple, Transparent
            <span className="block bg-gradient-to-r from-emerald-400 to-emerald-300 bg-clip-text text-transparent">
              Pricing
            </span>
          </motion.h1>

          <motion.p
            className="text-xl text-zinc-400 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Start free, upgrade when you're ready. No credit card required to get started.
          </motion.p>
        </motion.div>

        {/* Pricing Cards */}
        <motion.div
          className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-16"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {pricingTiers.map((tier, index) => (
            <motion.div
              key={tier.name}
              variants={fadeInUp}
              className="relative"
            >
              <GlassCard
                className={`
                  h-full p-8
                  ${tier.highlight 
                    ? 'border-emerald-500/50 shadow-[0_0_40px_rgba(16,185,129,0.15)]' 
                    : 'border-zinc-800/50'
                  }
                  ${tier.highlight ? 'bg-zinc-900/70' : 'bg-zinc-900/50'}
                  transition-all duration-300
                  hover:border-zinc-700
                  ${tier.highlight ? 'hover:border-emerald-500/70' : ''}
                `}
              >
                {/* Most Popular Badge */}
                {tier.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1.5 bg-emerald-500 text-zinc-950 text-sm font-semibold rounded-full">
                      {tier.badge}
                    </span>
                  </div>
                )}

                {/* Tier Header */}
                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-zinc-100 mb-2">
                    {tier.name}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-5xl font-bold text-zinc-100">
                      {tier.price}
                    </span>
                    {tier.price !== '$0' && (
                      <span className="text-zinc-400">/month</span>
                    )}
                  </div>
                  <p className="text-zinc-400">{tier.description}</p>
                </div>

                {/* Features List */}
                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className="flex items-start gap-3"
                    >
                      {feature.available ? (
                        <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      ) : (
                        <Lock className="w-5 h-5 text-zinc-600 flex-shrink-0 mt-0.5" />
                      )}
                      <span
                        className={`flex-1 ${
                          feature.available
                            ? 'text-zinc-300'
                            : 'text-zinc-600'
                        }`}
                      >
                        {feature.text}
                        {feature.comingSoon && (
                          <span className="ml-2 text-xs text-emerald-400">
                            (Coming Soon)
                          </span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <Button
                  variant={tier.highlight ? 'primary' : 'secondary'}
                  size="lg"
                  className="w-full"
                  onClick={() => handleGetStarted(tier.tier)}
                >
                  Get Started
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </GlassCard>
            </motion.div>
          ))}
        </motion.div>

        {/* Additional Info */}
        <motion.div
          className="text-center max-w-3xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <p className="text-zinc-400 text-sm">
            All plans include access to our core features. Upgrade anytime to unlock premium features.
          </p>
        </motion.div>
      </div>

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onUpgrade={handleUpgrade}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default PricingPage;
